import type { AnalyticsGranularity } from "@loot-os/api-contract";

/**
 * The windows the Analytics page offers. A day is read in hourly buckets, longer windows in
 * daily ones: the same two granularities the rollup crons write.
 */
export const ANALYTICS_WINDOWS = ["24h", "7d", "30d", "90d"] as const;
export type AnalyticsWindow = (typeof ANALYTICS_WINDOWS)[number];

export const ANALYTICS_WINDOW_LABELS: Record<AnalyticsWindow, string> = {
  "24h": "24 hours",
  "7d": "7 days",
  "30d": "30 days",
  "90d": "90 days",
};

const WINDOW_DAYS: Record<Exclude<AnalyticsWindow, "24h">, number> = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
};

export const HOUR_MS = 60 * 60 * 1000;
export const DAY_MS = 24 * HOUR_MS;

export function isAnalyticsWindow(value: unknown): value is AnalyticsWindow {
  return typeof value === "string" && (ANALYTICS_WINDOWS as readonly string[]).includes(value);
}

/** How many buckets a window's chart draws: 24 hours, or the window's days. */
export function windowBucketCount(window: AnalyticsWindow): number {
  return window === "24h" ? 24 : WINDOW_DAYS[window];
}

/** Which rollup granularity a window reads. */
export function granularityOf(window: AnalyticsWindow): AnalyticsGranularity {
  return window === "24h" ? "hour" : "day";
}

function floorToHour(at: Date): Date {
  return new Date(Math.floor(at.getTime() / HOUR_MS) * HOUR_MS);
}

function floorToUtcDay(at: Date): Date {
  return new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate()));
}

function nextUtcDay(day: Date): Date {
  return new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate() + 1));
}

/**
 * Half-open [from, to) on UTC bucket boundaries. `to` is the boundary after `now`, so the
 * bucket in progress is included and the chart's last point is the current hour or today.
 */
export function windowRange(window: AnalyticsWindow, now: Date): { from: Date; to: Date } {
  if (window === "24h") {
    const to = new Date(floorToHour(now).getTime() + HOUR_MS);
    return { from: new Date(to.getTime() - 24 * HOUR_MS), to };
  }
  const to = nextUtcDay(floorToUtcDay(now));
  const days = WINDOW_DAYS[window];
  return {
    from: new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth(), to.getUTCDate() - days)),
    to,
  };
}

/** Every bucket start in [from, to), so the chart has no holes. */
export function enumerateBuckets(granularity: AnalyticsGranularity, from: Date, to: Date): Date[] {
  const buckets: Date[] = [];
  if (granularity === "hour") {
    for (let t = floorToHour(from).getTime(); t < to.getTime(); t += HOUR_MS) {
      buckets.push(new Date(t));
    }
    return buckets;
  }
  for (let day = floorToUtcDay(from); day < to; day = nextUtcDay(day)) buckets.push(day);
  return buckets;
}

/**
 * Which buckets carry an x-axis label: every sixth hour, every day of a week, and only the
 * edges of a dense month or quarter. Undefined leaves the choice to the chart.
 */
export function axisTicks(keys: string[]): string[] | undefined {
  if (keys.length <= 7) return undefined;
  if (keys.length <= 24) return keys.filter((_, i) => i % 6 === 0);
  const first = keys.at(0);
  const last = keys.at(-1);
  return first && last ? [first, last] : undefined;
}

/** Tooltip label, pinned UTC: hours as "Aug 21, 14:00 UTC", days as "Aug 21, 2026". */
export function bucketLabel(granularity: AnalyticsGranularity, bucket: Date): string {
  if (granularity === "hour") {
    const day = bucket.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
    const hour = String(bucket.getUTCHours()).padStart(2, "0");
    return `${day}, ${hour}:00 UTC`;
  }
  return bucket.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** Compact x-axis tick: hours as "14:00", days as "Aug 21". */
export function tickLabel(granularity: AnalyticsGranularity, bucket: Date): string {
  if (granularity === "hour") return `${String(bucket.getUTCHours()).padStart(2, "0")}:00`;
  return bucket.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}
